import { useState } from 'react';
import { Copy, Check, Eye, EyeOff, ChevronDown, ChevronUp, RefreshCw, Loader2, Key } from 'lucide-react';
import Modal from './Modal';

const API_ENDPOINT = 'https://us-central1-houdashboard.cloudfunctions.net/quickAddToInbox';

export default function IOSShortcutSetupModal({ isOpen, onClose, apiKey, onGenerateKey }) {
  const [showKey, setShowKey] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState(null);

  // Use newly generated key if available, otherwise use the one from props
  const displayKey = newlyGeneratedKey || apiKey;

  const handleCopyUrl = async () => {
    await navigator.clipboard.writeText(API_ENDPOINT);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleCopyKey = async () => {
    if (!displayKey) return;
    await navigator.clipboard.writeText(displayKey);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const handleGenerateKey = async () => {
    setGenerating(true);
    try {
      const key = await onGenerateKey();
      setNewlyGeneratedKey(key);
      setShowKey(true); // Auto-reveal newly generated key
    } catch (error) {
      console.error('Failed to generate API key:', error);
    } finally {
      setGenerating(false);
    }
  };

  const maskedKey = displayKey
    ? displayKey.substring(0, 8) + '•'.repeat(48) + displayKey.substring(56)
    : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="iOS Shortcuts Setup">
      <div className="space-y-5">
        {/* Intro */}
        <p className="text-sm text-text-secondary">
          Add tasks to your inbox directly from your iPhone using Siri or the Shortcuts app.
        </p>

        {/* API Endpoint */}
        <div>
          <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wide mb-2">
            API Endpoint
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 px-3 py-2.5 bg-surface-elevated border border-border rounded-lg text-xs text-text-primary font-mono overflow-x-auto whitespace-nowrap">
              {API_ENDPOINT}
            </div>
            <button
              onClick={handleCopyUrl}
              className="p-2.5 bg-surface-elevated border border-border rounded-lg text-text-secondary hover:text-accent hover:border-accent transition-all cursor-pointer"
              title="Copy URL"
            >
              {copiedUrl ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
            </button>
          </div>
        </div>

        {/* API Key */}
        <div>
          <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wide mb-2">
            Your API Key
          </label>

          {!displayKey ? (
            // No key yet - show generate button
            <div className="p-4 bg-surface-elevated border border-border rounded-lg text-center">
              <Key size={24} className="mx-auto mb-2 text-text-tertiary" />
              <p className="text-sm text-text-secondary mb-3">
                Generate a personal API key to use iOS Shortcuts
              </p>
              <button
                onClick={handleGenerateKey}
                disabled={generating}
                className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium border-none cursor-pointer hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 mx-auto"
              >
                {generating ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Key size={14} />
                    Generate API Key
                  </>
                )}
              </button>
            </div>
          ) : (
            // Has key - show it with controls
            <>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2.5 bg-surface-elevated border border-border rounded-lg text-xs text-text-primary font-mono overflow-x-auto whitespace-nowrap">
                  {showKey ? displayKey : maskedKey}
                </div>
                <button
                  onClick={() => setShowKey(!showKey)}
                  className="p-2.5 bg-surface-elevated border border-border rounded-lg text-text-secondary hover:text-accent hover:border-accent transition-all cursor-pointer"
                  title={showKey ? "Hide key" : "Show key"}
                >
                  {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button
                  onClick={handleCopyKey}
                  className="p-2.5 bg-surface-elevated border border-border rounded-lg text-text-secondary hover:text-accent hover:border-accent transition-all cursor-pointer"
                  title="Copy key"
                >
                  {copiedKey ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                </button>
              </div>

              {/* Regenerate button */}
              <button
                onClick={handleGenerateKey}
                disabled={generating}
                className="mt-2 text-xs text-text-tertiary hover:text-accent transition-colors flex items-center gap-1 cursor-pointer bg-transparent border-none p-0"
              >
                {generating ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <RefreshCw size={12} />
                )}
                Regenerate key
              </button>

              {newlyGeneratedKey && (
                <p className="mt-2 text-xs text-green-500 flex items-center gap-1">
                  <Check size={12} />
                  New key generated! Make sure to update your shortcut.
                </p>
              )}
            </>
          )}
        </div>

        {/* Setup Instructions */}
        <div className="border border-border rounded-xl overflow-hidden">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="w-full flex items-center justify-between p-4 bg-surface-hover text-left cursor-pointer border-none"
          >
            <span className="text-sm font-medium text-text-primary">Setup Instructions</span>
            {showInstructions ? (
              <ChevronUp size={18} className="text-text-tertiary" />
            ) : (
              <ChevronDown size={18} className="text-text-tertiary" />
            )}
          </button>

          {showInstructions && (
            <div className="p-4 space-y-4 border-t border-border">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-white text-xs font-semibold flex items-center justify-center">
                  1
                </div>
                <div>
                  <p className="text-sm text-text-primary font-medium">Create a new shortcut</p>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    Open the Shortcuts app and tap + to create a new shortcut. Name it <strong>"Add to Inbox"</strong>.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-white text-xs font-semibold flex items-center justify-center">
                  2
                </div>
                <div>
                  <p className="text-sm text-text-primary font-medium">Add "Dictate Text" action</p>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    Search for <strong>"Dictate Text"</strong> and add it. This lets you speak your task directly. Set "Stop Listening" to <strong>After Pause</strong>.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-white text-xs font-semibold flex items-center justify-center">
                  3
                </div>
                <div>
                  <p className="text-sm text-text-primary font-medium">Add "Get Contents of URL" action</p>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    Search for "Get Contents of URL" and add it. Paste the API endpoint URL above.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-white text-xs font-semibold flex items-center justify-center">
                  4
                </div>
                <div>
                  <p className="text-sm text-text-primary font-medium">Configure the request</p>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    Tap "Show More" and set:
                  </p>
                  <ul className="text-xs text-text-tertiary mt-1 space-y-1 ml-3">
                    <li>• <strong>Method:</strong> POST</li>
                    <li>• <strong>Headers:</strong> Add <code className="bg-surface-elevated px-1 rounded">x-api-key</code> → paste your API key</li>
                    <li>• <strong>Request Body:</strong> JSON</li>
                    <li>• <strong>Body:</strong> Add key <code className="bg-surface-elevated px-1 rounded">title</code> → select <strong>"Dictated Text"</strong> variable</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-white text-xs font-semibold flex items-center justify-center">
                  5
                </div>
                <div>
                  <p className="text-sm text-text-primary font-medium">Add confirmation (optional)</p>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    Add "Show Notification" with title "Added to Inbox" and message set to the <strong>"Dictated Text"</strong> variable.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs font-semibold flex items-center justify-center">
                  ✓
                </div>
                <div>
                  <p className="text-sm text-text-primary font-medium">Use it!</p>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    Say <strong>"Hey Siri, Add to Inbox"</strong> → speak your task → it's added!
                  </p>
                </div>
              </div>

              {/* Alternative: Text input */}
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-text-tertiary">
                  <strong className="text-text-secondary">Prefer typing?</strong> Use "Ask for Input" instead of "Dictate Text" in step 2.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Quick Test */}
        {displayKey && (
          <div className="p-4 bg-surface-hover rounded-xl">
            <p className="text-xs text-text-tertiary mb-2">
              <strong className="text-text-secondary">Pro tip:</strong> You can also use a GET request with query parameters for simpler shortcuts:
            </p>
            <code className="block text-[10px] text-text-tertiary font-mono bg-surface-elevated p-2 rounded-lg overflow-x-auto whitespace-nowrap">
              {API_ENDPOINT}?title=Your+task+here&key=YOUR_API_KEY
            </code>
          </div>
        )}
      </div>
    </Modal>
  );
}
